import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { lazy } from 'react'
import { ProtectedRoute } from '@/auth/protected-route'

// Lazy-load every top-level page. Saurava statically imports everything; that
// doesn't scale to Varri's page count.
const LoginPage = lazy(() => import('@/features/auth/login-page'))
const DashboardPage = lazy(() => import('@/features/dashboard/dashboard-page'))
const DrilldownPage = lazy(() => import('@/features/dashboard/drilldown/drilldown-page'))
const FarmerListPage = lazy(() => import('@/features/farmers/list-page'))
const FarmerDetailPage = lazy(() => import('@/features/farmers/detail-page'))
const LogbookExportPage = lazy(() => import('@/features/reports/logbook-export-page'))
const ExecutivesPage = lazy(() => import('@/features/staff/executives-page'))
const ExecutiveDetailPage = lazy(() => import('@/features/staff/executive-detail-page'))
const VolunteersPage = lazy(() => import('@/features/staff/volunteers-page'))
const ValidatorsPage = lazy(() => import('@/features/validators/validators-page'))
const LiveMapPage = lazy(() => import('@/features/field-ops/live-map-page'))
const SettingsPage = lazy(() => import('@/features/settings/settings-page'))
const ValidationQueuePage = lazy(() => import('@/features/validation/queue-page'))
const AccessDeniedPage = lazy(() => import('@/features/auth/access-denied-page'))
const NotFoundPage = lazy(() => import('@/features/system/not-found-page'))

// The AppShell wraps every authenticated route (topbar + sidebar + main).
const AppShell = lazy(() => import('@/components/layout/app-shell'))

const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/forbidden', element: <AccessDeniedPage /> },

  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },

          {
            path: 'dashboard',
            element: <ProtectedRoute capability="view.dashboard" />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: 'farmers', element: <DrilldownPage /> },
            ],
          },

          {
            path: 'farmers',
            element: <ProtectedRoute capability="view.farmers" />,
            children: [
              { index: true, element: <FarmerListPage /> },
              { path: ':farmerId', element: <FarmerDetailPage /> },
            ],
          },

          {
            path: 'reports',
            element: <ProtectedRoute capability="view.reports" />,
            children: [
              { index: true, element: <Navigate to="logbook" replace /> },
              { path: 'logbook', element: <LogbookExportPage /> },
            ],
          },

          {
            path: 'validation/queue',
            element: <ProtectedRoute capability="approve.farmer" />,
            children: [{ index: true, element: <ValidationQueuePage /> }],
          },

          {
            // Old standalone /agreements page is now folded into the review
            // queue as a phase filter (2026-08-25 Srikar feedback). Redirect
            // preserves any external links / bookmarks admins may have.
            path: 'agreements',
            element: <Navigate to="/validation/queue?phase=AGREEMENT" replace />,
          },

          {
            path: 'executives',
            element: <ProtectedRoute capability="view.staff" />,
            children: [
              { index: true, element: <ExecutivesPage /> },
              { path: ':id', element: <ExecutiveDetailPage /> },
            ],
          },

          {
            path: 'volunteers',
            element: <ProtectedRoute capability="view.staff" />,
            children: [{ index: true, element: <VolunteersPage /> }],
          },

          {
            path: 'validators',
            element: <ProtectedRoute capability="view.staff" />,
            children: [{ index: true, element: <ValidatorsPage /> }],
          },

          {
            path: 'field-ops',
            element: <ProtectedRoute capability="view.field-ops" />,
            children: [{ path: 'live-map', element: <LiveMapPage /> }],
          },

          {
            path: 'settings',
            element: <ProtectedRoute capability="manage.settings" />,
            children: [{ index: true, element: <SettingsPage /> }],
          },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]

export const router = createBrowserRouter(routes)
